import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import UserService from "services/UserService.js";
import PasswordService from "services/PasswordService.js";
import InputErrorMessage from "components/utils/InputErrorMessage.js";
import RegexUtils from "utils/RegexUtils.js";
import COUNTRY_CODES_LIST from "utils/constants/CountryCodesList.js";
import useAPI from "hooks/useAPI.js";
import styles from "styles/settings.module.css";
import { useSnackbar } from "notistack";
import SnackbarConstants from "utils/constants/Snackbar.js";
import { useSelector, useDispatch } from 'react-redux';
import { selectAuthUser, updateAuthUser } from "state/slices/authUserSlice";

function Profile() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isPasswordProcessing, setIsPasswordProcessing] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const updateProfileAPI = useAPI(UserService.updateProfile, updateProfileHandler, true, false);

  const authUser = useSelector(selectAuthUser);
  const dispatch = useDispatch();

  const { register, formState: {
    errors
  }, handleSubmit, setValue, reset } = useForm({ criteriaMode: "all", mode: "onTouched" });

  const {
    register: registerPassword,
    formState: { errors: passwordErrors },
    handleSubmit: handlePasswordSubmit,
    watch,
    reset: resetPasswordForm,
  } = useForm({ criteriaMode: "all", mode: "onTouched" });

  useEffect(() => {
    // Set form data from the authenticated user state objectz
    reset(authUser)
  }, [authUser]);


  function updateProfile(data) {
    updateProfileAPI.execute(data);
  }

  /* API success handlers */

  function updateProfileHandler(response) {
    dispatch(updateAuthUser(response.data));
  }

  async function changePassword(data) {
    setIsPasswordProcessing(true);

    try {
      const response = await PasswordService.changePassword(data);
      enqueueSnackbar(response.message, SnackbarConstants.success);
      resetPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      let errorData = error?.data;
      if (errorData?.message) {
        enqueueSnackbar(errorData.message, SnackbarConstants.error);
        let apiErrors = errorData.errors;
        if (apiErrors) {
          apiErrors.forEach((item) => {
            enqueueSnackbar(item, SnackbarConstants.error);
          });
        }
      } else {
        enqueueSnackbar("Oops! Something went wrong", SnackbarConstants.error);
      }
    }

    setIsPasswordProcessing(false);
  }

  return (<Paper elevation={0} className="px-5 py-4">
    <h4>Profile</h4>
    <form className="mt-4" onSubmit={handleSubmit(updateProfile)}>
      <div className="mb-3">
        <label htmlFor="firstNameInput" className="form-label">
          First Name
        </label>
        <input type="text" className="form-control" id="firstNameInput" {...register("firstName", {
          required: "First Name is required",
          pattern: {
            value: RegexUtils.NAME,
            message: "Fist Name is invalid"
          }
        })} />
        <InputErrorMessage name="firstName" errors={errors} />
      </div>
      <div className="mb-3">
        <label htmlFor="lastNameInput" className="form-label">
          Last Name
        </label>
        <input type="text" className="form-control" id="lastNameInput" {...register("lastName", {
          required: "Last Name is required",
          pattern: {
            value: RegexUtils.NAME,
            message: "Last Name is invalid"
          }
        })} />
        <InputErrorMessage name="lastName" errors={errors} />
      </div>
      <div className="mb-3">
        <label htmlFor="emailInput" className="form-label">
          Email ID
        </label>
        <input type="email" className="form-control" id="emailInput" {...register("email", {
          required: "Email is required",
          pattern: {
            value: RegexUtils.EMAIL,
            message: "Email is invalid"
          }
        })} disabled />
        <InputErrorMessage name="email" errors={errors} />
      </div>
      <div className="mb-3">
        <label htmlFor="countryCode" className="form-label">
          Country Code
        </label>
        <select className="form-select" id="countryCodeInput" aria-label="Default select example" {...register("countryCode", {
          required: "Country Code is required",
          minLength: {
            value: 2,
            message: "Country Code must exceed 2 characters"
          },
          maxLength: {
            value: 5,
            message: "Country Code must not exceed 5 characters"
          }
        })}>
          <option></option>
          {
            COUNTRY_CODES_LIST.map((item, index) => (<option key={item.value + index} value={item.value}>
              {item.country}
            </option>))
          }
        </select>
        <InputErrorMessage name="countryCode" errors={errors} />
      </div>
      <div className="mb-4">
        <label htmlFor="phoneInput" className="form-label">
          Phone
        </label>
        <input type="text" className="form-control" id="phoneInput" {...register("phone", {
          required: "Phone is required",
          pattern: {
            value: RegexUtils.PHONE,
            message: "Phone is invalid"
          }
        })} />
        <InputErrorMessage name="phone" errors={errors} />
      </div>
      {
        (updateProfileAPI.status === 'pending')
          ? (<LoadingButton loading variant="contained" className="me-3">
            Updating
          </LoadingButton>)
          : (<Button type="submit" className="me-3" variant="contained">
            Update
          </Button>)
      }
      <Button onClick={() => navigate(0)} variant="outlined">
        Cancel
      </Button>
    </form>

    <Divider sx={{ my: 4 }} />

    <div className={styles["security-card"]}>
      <Typography variant="h5" className={styles["security-title"]}>Security</Typography>
      <Typography variant="body2" color="text.secondary" className={styles["security-subtitle"]}>
        Update your password here to keep your account secure.
      </Typography>

      <form onSubmit={handlePasswordSubmit(changePassword)}>
        <div className={styles["security-form-group"]}>
          <label htmlFor="currentPasswordInput" className={`form-label ${styles["security-label"]}`}>
            Current Password
          </label>
          <OutlinedInput
            id="currentPasswordInput"
            className={`${styles["password-input"]} ${styles["security-input"]}`}
            autoComplete="current-password"
            type={showCurrentPassword ? "text" : "password"}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle current password visibility"
                  onClick={() => setShowCurrentPassword((show) => !show)}
                  edge="end"
                >
                  {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            {...registerPassword("currentPassword", {
              required: "Current Password is required",
              pattern: {
                value: RegexUtils.PASSWORD,
                message: "Current Password is invalid"
              }
            })}
          />
          <InputErrorMessage name="currentPassword" errors={passwordErrors} />
        </div>

        <div className={styles["security-form-group"]}>
          <label htmlFor="newPasswordInput" className={`form-label ${styles["security-label"]}`}>
            New Password
          </label>
          <OutlinedInput
            id="newPasswordInput"
            className={`${styles["password-input"]} ${styles["security-input"]}`}
            autoComplete="new-password"
            type={showNewPassword ? "text" : "password"}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle new password visibility"
                  onClick={() => setShowNewPassword((show) => !show)}
                  edge="end"
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            {...registerPassword("newPassword", {
              required: "New Password is required",
              pattern: {
                value: RegexUtils.PASSWORD,
                message: "New Password is invalid"
              }
            })}
          />
          <InputErrorMessage name="newPassword" errors={passwordErrors} />
          <div id="passwordHelpBlock" className={`form-text ${styles['password-help-text']}`}>
            Password must be atleast 8 characters long, including atleast one letter, one number, and one special character
          </div>
        </div>

        <div className={styles["security-form-group"]}>
          <label htmlFor="confirmPasswordInput" className={`form-label ${styles["security-label"]}`}>
            Confirm Password
          </label>
          <OutlinedInput
            id="confirmPasswordInput"
            className={`${styles["password-input"]} ${styles["security-input"]}`}
            autoComplete="new-password"
            type={showConfirmPassword ? "text" : "password"}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword((show) => !show)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            {...registerPassword("confirmPassword", {
              required: "Confirm Password is required",
              validate: (value) =>
                value === watch("newPassword") || "Confirm Password must match New Password",
            })}
          />
          <InputErrorMessage name="confirmPassword" errors={passwordErrors} />
        </div>

        <div className={styles["security-button-row"]}>
          {isPasswordProcessing ? (
            <LoadingButton loading variant="contained">
              Change Password
            </LoadingButton>
          ) : (
            <Button type="submit" variant="contained">
              Change Password
            </Button>
          )}
        </div>
      </form>
    </div>
  </Paper>);
}

export default Profile;
